// src/App.js
import React, { useEffect } from 'react';
import { CssBaseline, CircularProgress, Box, Typography, Button } from '@mui/material';

// Contexts
import { db } from './firebase';
import { doc, getDoc } from 'firebase/firestore';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { CustomerAuthProvider, useCustomerAuth } from './contexts/CustomerAuthContext';
import { NavigationProvider, useNavigation } from './contexts/NavigationContext';

// Pages / Components
import LoginPortal from './components/LoginPortal';
import AdminLoginPage from './components/AdminLoginPage';
import EngineerLoginPage from './components/EngineerLoginPage';
import CustomerLoginPage from './components/CustomerLoginPage';
import AdminLayout from './components/AdminLayout';
import EngineerDashboard from './components/EngineerDashboard';
import EngineerJobDetail from './components/EngineerJobDetail';
import EngineerSettings from './components/EngineerSettings';
import JobDetailPage from './components/JobDetailPage';
import JobViewer from './components/JobViewer';
import AdminSettings from './components/AdminSettings';
import CreateJob from './components/CreateJob';
import LiveJobFeed from './components/LiveJobFeed';
import ClientManagement from './components/ClientManagement';
import ClientDetailPage from './components/ClientDetailPage';
import EngineerManagement from './components/EngineerManagement';
import EngineerDetailPage from './components/EngineerDetailPage';
import LiveMap from './components/LiveMap';
import JobSheetDesigner from './components/JobSheetDesigner';
import InvoicePage from './components/InvoicePage';

// Customer Portal Components
import CustomerLayout from './components/CustomerLayout';
import JobRequests from './components/JobRequests';
import CustomerDashboard from './components/CustomerDashboard';
import CustomerRequestJob from './components/CustomerRequestJob';
import CustomerJobList from './components/CustomerJobList';
import CustomerInvoices from './components/CustomerInvoices';
import CustomerVehicles from './components/CustomerVehicles';
import CustomerDocuments from './components/CustomerDocuments';
import CustomerSettings from './components/CustomerSettings';

// Bulk Portal Enable
import BulkPortalEnable from './components/BulkPortalEnable';
import NotificationHandler from './components/NotificationHandler';


// Loading component
const LoadingScreen = ({ message = "Loading Your Job System..." }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100vh',
      backgroundColor: '#0f172a',
      gap: 2,
    }}
  >
    <CircularProgress size={48} />
    <Typography variant="h5" color="text.primary">
      {message}
    </Typography>
  </Box>
);

// Error component
const ErrorScreen = ({ title, message, onLogout }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100vh',
      backgroundColor: '#0f172a',
      gap: 2,
      p: 3,
    }}
  >
    <Typography variant="h4" color="error.main" gutterBottom>
      {title}
    </Typography>
    <Typography variant="body1" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
      {message}
    </Typography>
    <Button
      variant="contained"
      color="error"
      onClick={onLogout}
    >
      Log Out
    </Button>
  </Box>
);

// -----------------------------------------------
// ADMIN DASHBOARD COMPONENT
// -----------------------------------------------
function AdminDashboard() {
  const { logout } = useAuth();
  const {
    adminPage,
    selectedJobId,
    selectedClientId,
    selectedEngineerId,
    createJobData,
    navigateToAdminPage,
    selectJob,
    selectClient,
    selectEngineer,
    goBack,
  } = useNavigation();

  // Render detail views
  if (selectedJobId) {
    return <JobViewer jobId={selectedJobId} onBack={goBack} />;
  }

  if (selectedClientId) {
    return <ClientDetailPage clientId={selectedClientId} onBack={goBack} />;
  }

  if (selectedEngineerId) {
    return <EngineerDetailPage engineerId={selectedEngineerId} onBack={goBack} />;
  }

  // Render main admin pages
  const renderPage = () => {
    switch (adminPage) {
      case 'dashboard':
        return (
          <LiveJobFeed
            onJobSelect={selectJob}
            onCreateJob={() => navigateToAdminPage('createJob')}
          />
        );
      case 'liveMap':
        return <LiveMap />;
      case 'jobRequests':
        return <JobRequests />;
      case 'createJob':
        return <CreateJob initialData={createJobData} />;
      case 'clients':
        return <ClientManagement onClientSelect={selectClient} />;
      case 'engineers':
        return <EngineerManagement onEngineerSelect={selectEngineer} />;
      case 'bulkPortal':
        return <BulkPortalEnable />;
      case 'designer':
        return <JobSheetDesigner />;
      case 'invoices':
        return <InvoicePage />;
      case 'settings':
        return <AdminSettings />;
      default:
        return (
          <LiveJobFeed
            onJobSelect={selectJob}
            onCreateJob={() => navigateToAdminPage('createJob')}
          />
        );
    }
  };

  return (
    <AdminLayout
      key={adminPage}
      currentPage={adminPage}
      onNavigate={navigateToAdminPage}
      onLogout={logout}
    >
      {adminPage === 'invoices' ? <InvoicePage key="invoices-page" /> : renderPage()}
    </AdminLayout>
  );
}

// -----------------------------------------------
// ENGINEER DASHBOARD COMPONENT
// -----------------------------------------------
function EngineerDashboardWrapper() {
  const { user, logout } = useAuth();
  const { selectedJobId, selectJob, goBack } = useNavigation();
  const [showSettings, setShowSettings] = React.useState(false);

  // Engineer viewing settings
  if (showSettings) {
    return <EngineerSettings onBack={() => setShowSettings(false)} />;
  }

  // Engineer viewing job detail
  if (selectedJobId) {
    return <EngineerJobDetail jobId={selectedJobId} onBack={goBack} />;
  }

  // Engineer dashboard
  return (
    <EngineerDashboard
      user={user}
      onLogout={logout}
      onJobSelect={selectJob}
      onSettings={() => setShowSettings(true)}
    />
  );
}

// -----------------------------------------------
// CUSTOMER PORTAL COMPONENT
// -----------------------------------------------
function CustomerPortalWrapper() {
  const { customer, logoutCustomer } = useCustomerAuth();
  const [customerPage, setCustomerPage] = React.useState('dashboard');

  const renderCustomerPage = () => {
    switch (customerPage) {
      case 'dashboard':
        return <CustomerDashboard onNavigate={setCustomerPage} />;
      case 'requestJob':
        return <CustomerRequestJob onSuccess={() => setCustomerPage('dashboard')} />;
      case 'jobs':
        return <CustomerJobList />;
      case 'invoices':
        return <CustomerInvoices />;
      case 'vehicles':
        return <CustomerVehicles />;
      case 'documents':
        return <CustomerDocuments />;
      case 'settings':
        return <CustomerSettings />;
      default:
        return <CustomerDashboard onNavigate={setCustomerPage} />;
    }
  };

  return (
    <CustomerLayout
      currentPage={customerPage}
      onNavigate={setCustomerPage}
      onLogout={logoutCustomer}
    >
      {renderCustomerPage()}
    </CustomerLayout>
  );
}

// -----------------------------------------------
// MAIN APP CONTENT (uses contexts)
// -----------------------------------------------
function AppContent() {
  const { user, userRole, loading, error, logout, isAdmin, isEngineer } = useAuth();
  const { customer, setCustomer } = useCustomerAuth();
  const [selectedPortal, setSelectedPortal] = React.useState(null);

  // EFFECT TO BRIDGE FIREBASE AUTH & CUSTOMER CONTEXT
  // This is the critical fix. When a Firebase user logs in with a 'customer' role,
  // this effect fetches their corresponding customer document and sets it in the
  // CustomerAuthContext, which then allows the app to render the customer portal.
  useEffect(() => {
    const syncCustomerData = async () => {
      // If we have a Firebase user with a customer role, but no customer object in the context yet...
      if (user && userRole === 'customer' && !customer) {
        try {
          const idTokenResult = await user.getIdTokenResult(true); // Force refresh of token
          const customerId = idTokenResult.claims.customerId;

          if (customerId) {
            const customerDocRef = doc(db, 'customers', customerId);
            const customerDocSnap = await getDoc(customerDocRef);
            if (customerDocSnap.exists()) {
              // Set the customer data in the context, triggering the portal to load
              setCustomer({ id: customerDocSnap.id, ...customerDocSnap.data() });
            } else {
              console.error(`CRITICAL: Firestore document for customerId ${customerId} not found.`);
              logout(); // Log out the user as their data is inconsistent
            }
          } else {
            console.error('CRITICAL: User has customer role but is missing the customerId claim.');
            logout(); // Log out the user as their auth token is invalid
          }
        } catch (e) {
          console.error("Error syncing customer data:", e);
          logout();
        }
      }
    };
    syncCustomerData();
  }, [user, userRole, customer, setCustomer, logout]);

  // Loading state
  if (loading) {
    return <LoadingScreen />;
  }

  // If the customer object is set (by the effect above), show the portal.
  if (customer) {
    return <CustomerPortalWrapper />;
  }

  // No user - show login portal selection or specific login
  if (!user) {
    // Show portal selection
    if (!selectedPortal) {
      return <LoginPortal onSelectPortal={setSelectedPortal} />;
    }
    
    // Show specific login based on selection
    switch (selectedPortal) {
      case 'admin':
        return <AdminLoginPage onBack={() => setSelectedPortal(null)} />;
      case 'engineer':
        return <EngineerLoginPage onBack={() => setSelectedPortal(null)} />;
      case 'customer':
        return <CustomerLoginPage onBack={() => setSelectedPortal(null)} />;
      default:
        return <LoginPortal onSelectPortal={setSelectedPortal} />;
    }
  }

  // Show a loading screen while we are fetching the customer data after login
  if (user && userRole === 'customer' && !customer) {
    return <LoadingScreen message="Loading Customer Portal..." />;
  }

  // User exists but has error or no role
  if (error || !userRole) {
    return (
      <ErrorScreen
        title="Access Error"
        message={
          error ||
          "No role assigned to your account. Please contact an administrator."
        }
        onLogout={logout}
      />
    );
  }

  // Admin flow - NotificationHandler runs silently in background
  if (isAdmin()) {
    return (
      <>
        <NotificationHandler user={user} />
        <AdminDashboard />
      </>
    );
  }

  // Engineer flow - NotificationHandler runs silently in background
  if (isEngineer()) {
    return (
      <>
        <NotificationHandler user={user} />
        <EngineerDashboardWrapper />
      </>
    );
  }

  // Fallback - should never reach here
  return (
    <ErrorScreen
      title="Unknown Error"
      message="An unexpected error occurred. Please try logging in again."
      onLogout={logout}
    />
  );
}

// -----------------------------------------------
// ROOT APP COMPONENT (provides contexts)
// -----------------------------------------------
function App() {
  return (
    <AuthProvider>
      <CustomerAuthProvider>
        <NavigationProvider>
          <AppContent />
        </NavigationProvider>
      </CustomerAuthProvider>
    </AuthProvider>
  );
}

export default App;